import { useState, useEffect } from "react";
import {
  Zap, Code2, GitBranch, Terminal, CheckCircle2,
  Cpu, AlertTriangle, BrainCircuit, Gamepad2, Globe, Mail,
  ShieldCheck, Lock, Cloud, ArrowRight, Copy, Check, Sun, Moon
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const cn = (...classes) => classes.filter(Boolean).join(" ");

const ROLE_DATA = [
  {
    label: "App Developer", article: "an", project: "SaaS Dashboard V2",
    primaryThrust: "You abandoned the AuthGuard.ts file at 2:00 AM with broken JWT validation logic:",
    tasks: ["Complete the token refresh cycle before the session times out.", "The middleware is currently blocking valid requests — fix line 42.", "Sync the new environment variables to your local .env file."],
    risk: "Critical: You hardcoded a SECRET_KEY in the commit history 10 minutes ago.",
    inputs: [{ icon: Code2, label: "3 Files Changed", color: "text-blue-400" }, { icon: GitBranch, label: "PR #402 Pending", color: "text-zinc-300" }, { icon: Terminal, label: "Build Passing", color: "text-emerald-400" }]
  },
  {
    label: "Freelancer", article: "a", project: "Client: Starline Inc.",
    primaryThrust: "The client just emailed asking for a quick tweak to the video player, but your contract is fixed-bid:",
    tasks: ["Flag the request as Out of Scope using the saved template.", "Reply with the hourly rate estimation for the video feature.", "Do not touch the code until the addendum is signed."],
    risk: "Scope Creep: This tweak requires a database migration you are not charging for.",
    inputs: [{ icon: Globe, label: "Staging Updated", color: "text-indigo-400" }, { icon: Code2, label: "Invoice Generated", color: "text-zinc-300" }, { icon: Mail, label: "Email Scanned", color: "text-amber-400" }]
  },
  {
    label: "Game Developer", article: "a", project: "Cyberpunk RPG",
    primaryThrust: "The new particle system you added to Sector 4 is tanking the frame rate on the Steam Deck build:",
    tasks: ["Reduce the particle count or switch to GPU instancing.", "The texture streaming pool is overflowing — compress the skybox.", "Check the physics collider on the new NPC; it is too complex."],
    risk: "Performance: Frame time spiked to 32ms. Target is 16ms in the last playtest.",
    inputs: [{ icon: Gamepad2, label: "Unity Editor Active", color: "text-zinc-300" }, { icon: Code2, label: "C# Scripts Changed", color: "text-blue-400" }, { icon: Cpu, label: "Profiler: High Load", color: "text-red-400" }]
  }
];

const PROBLEMS = [
  { num: "01", topic: "CONTEXT DECAY", watermark: "CONTEXT DECAY", headline: "You finished something. Then life happened. Now you're a stranger to your own code.", desc: "Every interruption (a meeting, a weekend, a week off) costs you hours of mental reconstruction. You spend more time remembering than building.", quote: "I know I built this. I just can't remember why I did it this way." },

  { num: "02", topic: "THE 90% ILLUSION", watermark: "THE 90% ILLUSION", headline: "Tasks don't die. They just stop moving.", desc: "Progress feels real until it doesn't. A task that sits at 90% complete for two weeks isn't a task anymore, it's an anchor. It creates the feeling of work without the output of it. You plan around it as if it is done. Most projects don't fail at the start.", quote: "It's basically done. I just need to clean it up a little." },

  { num: "03", topic: "FEATURE CREEP", watermark: "FEATURE CREEP", headline: "The project you're building is not the project you started.", desc: "Every good idea feels free at the time. A small addition here, a while-we're-at-it there. None of them feel like scope creep in the moment; they feel like improvements. But each one adds invisible weight to every future decision. And by the time you notice, the original goal is buried under six months of good intentions. Feature creep first arrives as enthusiasm.", quote: "It's still the same app. We just added a few things." },

  { num: "04", topic: "DECISION FATIGUE", watermark: "DECISION FATIGUE", headline: "By 3pm you're making worse decisions than you were at 9am. And you don't notice.", desc: "Every micro-decision during a build day (which file to open, which bug to fix first, whether to refactor now or later) draws from the same cognitive budget. By midday that budget is depleted. You context-switch more, commit less, and spend increasing time on low-value decisions just because they feel easier. The result isn't dramatic failure. It's slow drift. A gradual shift from building to managing the act of building.", quote: "I'll figure out the architecture tomorrow when I'm fresher." },
	/*
  { num: "05", topic: "CONTEXT LOAD", watermark: "CONTEXT LOAD", headline: "Your brain is not a project management tool.", desc: "Experienced builders carry an enormous amount of implicit project knowledge in their heads at any given time. Which endpoints are stable. Which part of the codebase is dangerous to touch. What the original plan was before three pivots. This information lives nowhere permanent. It lives in working memory, and working memory has a hard ceiling. When it overflows, things fall through. Not because you're careless. Because no one was designed to hold all of it.", quote: "I knew I was going to forget about that dependency." }
  */
];

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handle = () => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={handle} className="ml-auto p-2 rounded-lg hover:bg-white/10 transition-colors text-zinc-500 hover:text-white">
      {copied ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
    </button>
  );
}

function ProblemBlock({ num, topic, watermark, headline, desc, quote, isLeft, d }) {
  return (
    <div className={cn("relative py-28 w-full overflow-hidden flex items-center border-b", d ? "border-zinc-900/60" : "border-zinc-200")}>
      <div className={cn("absolute top-1/2 -translate-y-1/2 text-[120px] md:text-[200px] font-black italic whitespace-nowrap pointer-events-none select-none tracking-tighter", isLeft ? "-left-10" : "-right-10", d ? "opacity-[0.03] text-white" : "opacity-[0.05] text-zinc-900")}>
        {watermark}
      </div>
      <div className={cn("max-w-7xl mx-auto w-full px-6 flex", isLeft ? "justify-start" : "justify-end")}>
        <div className="max-w-2xl relative z-10">
          <div className="mb-5 flex items-center gap-3">
            <div className="w-8 h-px bg-purple-400" />
            <span className="text-purple-400 font-bold text-xs tracking-widest uppercase">Problem {num}</span>
            <span className={cn("font-black text-xs tracking-widest uppercase", d ? "text-zinc-500" : "text-zinc-400")}>&mdash; {topic}</span>
          </div>
          <h3 className={cn("text-3xl md:text-5xl font-black tracking-tight mb-8 leading-[1.1]", d ? "text-white" : "text-zinc-900")}>{headline}</h3>
          <p className={cn("text-lg md:text-xl leading-relaxed mb-12", d ? "text-zinc-400" : "text-zinc-600")}>{desc}</p>
          <blockquote className="border-l-4 border-purple-400 pl-6 py-2">
            <p className={cn("text-lg italic font-medium", d ? "text-zinc-300" : "text-zinc-700")}>"{quote}"</p>
          </blockquote>
        </div>
      </div>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, desc, d }) {
  return (
    <div className={cn("p-8 rounded-2xl border h-full flex flex-col shadow-sm", d ? "bg-zinc-900 border-zinc-800 text-zinc-100" : "bg-white border-zinc-200 text-zinc-900")}>
      <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center mb-6", d ? "bg-purple-400/10 text-purple-400" : "bg-purple-50 text-purple-500")}>
        <Icon size={24} />
      </div>
      <h3 className="text-xl font-bold mb-3">{title}</h3>
      <p className={cn("text-sm leading-relaxed", d ? "text-zinc-400" : "text-zinc-600")}>{desc}</p>
    </div>
  );
}

export default function LandingPage() {
  const [isDark, setIsDark] = useState(() => {
    try {
      const stored = localStorage.getItem("thrust-theme");
      if (stored !== null) return stored === "dark";
    } catch {}
    return true;
  });
  const [roleIndex, setRoleIndex] = useState(0);
  const role = ROLE_DATA[roleIndex];
  const d = isDark;

  useEffect(() => {
    try { localStorage.setItem("thrust-theme", isDark ? "dark" : "light"); } catch {}
  }, [isDark]);

  useEffect(() => {
    const t = setInterval(() => setRoleIndex(p => (p + 1) % ROLE_DATA.length), 4300);
    return () => clearInterval(t);
  }, []);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className={cn("min-h-screen font-sans transition-colors duration-300 overflow-x-hidden", d ? "bg-zinc-950 text-zinc-50" : "bg-zinc-50 text-zinc-900")}>

      {/* NAV */}
      <nav className={cn("fixed top-0 w-full z-50 backdrop-blur-md border-b h-16 flex items-center transition-colors", d ? "bg-zinc-950/80 border-zinc-800" : "bg-white/80 border-zinc-200")}>
        <div className="flex justify-between items-center px-6 max-w-7xl mx-auto w-full">
          
          {/* LOGO */}
          <div className="flex items-center gap-2 font-black text-xl tracking-tighter italic">
            <img src="./thrust_tracing_white.png" height={160} width={160} alt="Thrust" className={cn("transition-opacity", d ? "opacity-100" : "opacity-80 invert")} />
            THRUST
          </div>

          <div className={cn("hidden md:flex items-center gap-8 text-sm font-bold tracking-tight", d ? "text-zinc-300" : "text-zinc-700")}>
            <a href="#how-it-works" className="hover:text-purple-400 transition-colors">How it Works</a>
            <a href="#install" className="hover:text-purple-400 transition-colors">Install Agent</a>
            <a href="#pricing" className="hover:text-purple-400 transition-colors">Pricing</a>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsDark(!isDark)}
              className={cn("p-2 rounded-lg transition-colors", d ? "hover:bg-zinc-800 text-zinc-400" : "hover:bg-zinc-100 text-zinc-500")}
            >
              {d ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <button onClick={() => scrollTo("install")} className="bg-purple-500 text-white px-5 py-2 rounded-lg text-sm font-bold shadow-lg shadow-purple-500/20 hover:bg-purple-400 transition-all">
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="px-6 pt-36 pb-24 text-center max-w-5xl mx-auto">
        <h1 className="text-5xl md:text-8xl font-black tracking-tight mb-6 leading-[0.9]">
          All {role.article}{" "}
          <span className="text-purple-400 relative inline-block text-left">
            <AnimatePresence mode="wait">
              <motion.span key={role.label} initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.4 }} className="block absolute top-0 left-0 whitespace-nowrap">
                {role.label}
              </motion.span>
            </AnimatePresence>
            <span className="invisible">{role.label}</span>
          </span>
          <br />needs is focus.
        </h1>
        <p className={cn("text-lg md:text-2xl mb-10 max-w-3xl mx-auto font-medium", d ? "text-zinc-400" : "text-zinc-500")}>
          You already know what needs to be built.<br className="hidden md:block" />
 The problem is remembering where you left off and what steps to take next.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a href="https://thrust.web.app/console" className="w-full sm:w-auto px-8 py-4 bg-purple-500 rounded-xl font-bold text-white hover:bg-purple-400 transition-all text-base shadow-xl shadow-purple-500/20 flex items-center justify-center gap-2">
            Go to Console <ArrowRight size={16} />
          </a>
          <button onClick={() => scrollTo("install")} className={cn("w-full sm:w-auto px-8 py-4 border rounded-xl font-bold transition-all text-base flex items-center justify-center gap-2", d ? "border-zinc-700 text-white hover:bg-zinc-800" : "border-zinc-300 text-zinc-900 hover:bg-zinc-100")}>
            Install Local Agent
          </button>
        </div>
      </section>

      {/* DASHBOARD DEMO */}
      <section className="px-6 pb-12 max-w-6xl mx-auto">
        <div className={cn("rounded-3xl p-2 border shadow-2xl", d ? "bg-zinc-900 border-zinc-800" : "bg-white border-zinc-200")}>
          <div className={cn("rounded-2xl overflow-hidden border min-h-[500px]", d ? "bg-zinc-950 border-zinc-800" : "bg-zinc-50 border-zinc-100")}>
            <div className={cn("h-10 border-b flex items-center px-4 justify-between", d ? "bg-zinc-900/50 border-zinc-800" : "bg-white border-zinc-100")}>
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-red-500/40" />
                <div className="w-2.5 h-2.5 rounded-full bg-amber-500/40" />
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/40" />
              </div>
              <div className={cn("text-[11px] font-bold uppercase tracking-widest", d ? "text-zinc-400" : "text-zinc-500")}>Morning Briefing from Thrust</div>
            </div>
            <div className="p-8 md:p-12 grid lg:grid-cols-[1.5fr_1fr] gap-12">
              <div className="space-y-8">
                <motion.div key={role.project} initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.2 }}>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="px-2 py-0.5 rounded bg-purple-400/10 text-purple-400 text-[11px] font-bold uppercase border border-purple-400/20">Active Context</span>
                    <span className={cn("text-sm font-mono", d ? "text-zinc-500" : "text-zinc-400")}>09:00 AM</span>
                  </div>
                  <h3 className="text-3xl font-black">{role.project}</h3>
                </motion.div>
                <div className={cn("p-6 rounded-2xl", d ? "bg-zinc-900 text-zinc-300" : "bg-white text-zinc-700 border border-zinc-200")}>
                  <p className="font-mono text-base mb-6 opacity-80">{role.primaryThrust}</p>
                  <ul className="space-y-4 text-base font-bold">
                    {role.tasks.map((t, i) => (
                      <li key={i} className="flex gap-4"><span className="text-purple-400 font-mono">0{i + 1}</span>{t}</li>
                    ))}
                  </ul>
                  <div className="mt-8 p-4 bg-red-500/5 border border-red-500/10 rounded-xl flex gap-3 items-start">
                    <AlertTriangle size={16} className="text-red-500 shrink-0 mt-0.5" />
                    <p className="text-sm font-bold text-red-500 leading-relaxed">{role.risk}</p>
                  </div>
                </div>
              </div>
              <div className={cn("space-y-8 lg:border-l lg:pl-12", d ? "border-zinc-800" : "border-zinc-200")}>
                <div>
                  <h4 className={cn("text-xs font-black uppercase tracking-widest mb-6", d ? "text-zinc-500" : "text-zinc-400")}>Inputs Analyzed</h4>
                  <div className="space-y-4">
                    {role.inputs.map((inp, i) => (
                      <div key={i} className={cn("flex items-center gap-4 p-3 rounded-xl border", d ? "bg-zinc-900/50 border-zinc-800" : "bg-white border-zinc-200 shadow-sm")}>
                        <div className={cn("p-2 rounded-lg", d ? "bg-zinc-800 text-white" : "bg-zinc-100 text-zinc-700")}><inp.icon size={18} /></div>
                        <span className={cn("text-sm font-bold", inp.color)}>{inp.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className={cn("text-xs font-black uppercase tracking-widest mb-4", d ? "text-zinc-500" : "text-zinc-400")}>Context Health</h4>
                  <div className={cn("h-2 w-full rounded-full overflow-hidden", d ? "bg-zinc-800" : "bg-zinc-200")}>
                    <div className="h-full bg-emerald-500 w-[92%] shadow-[0_0_15px_rgba(16,185,129,0.3)]" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM HEADER */}
      <section className={cn("pt-32 pb-0 border-t", d ? "border-zinc-900" : "border-zinc-200")}>
        <div className="text-center max-w-4xl mx-auto px-6 mb-4">
          <h2 className="text-sm font-black text-purple-400 uppercase tracking-widest mb-6">The Brutal Truth</h2>
        {/*  <h3 className="text-4xl md:text-6xl font-black mb-8 leading-[1.1]">
           Your project isn't failing.<br />
	 You're paying a <span className="italic underline decoration-purple-400">tax you're unaware of.</span>
	  
          </h3> */}
        </div>
      </section>

      {/* PROBLEM BLOCKS */}
      <section>
        {PROBLEMS.map((p, i) => <ProblemBlock key={p.num} {...p} isLeft={i % 2 === 0} d={d} />)}
      </section>

      {/* TRANSITION */}
      <section className={cn("py-32 px-6 border-y text-center", d ? "bg-purple-900/10 border-zinc-900" : "bg-purple-50 border-purple-100")}>
        <h2 className="text-4xl md:text-6xl font-black max-w-4xl mx-auto leading-tight tracking-tight">
          This is not a focus app.<br />This is <span className="text-purple-400">momentum, engineered.</span>
        </h2>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className={cn("py-32 px-6", d ? "bg-[#0A0A0F]" : "bg-white")}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-24">
            <h2 className="text-4xl md:text-5xl font-black mb-6">From Chaos to Flow State</h2>
            <p className={cn("text-xl max-w-2xl mx-auto", d ? "text-zinc-400" : "text-zinc-600")}>Four steps to eliminate context decay forever.</p>
          </div>
          <div className="grid md:grid-cols-4 gap-12">
            {[
              { step: "01", icon: Terminal, title: "Install and Point", desc: "Install the CLI and point it at your project folder. No config files, no cloud sync. It takes under two minutes." },
              { step: "02", icon: Code2, title: "Work Normally", desc: "You build. Thrust watches. It detects file changes, workspace activity and commit patterns. It adapts to your workflow." },
              { step: "03", icon: Zap, title: "Receive a Thrust", desc: "When you complete meaningful work, a Thrust arrives. One concise briefing. One clear next step." },
              { step: "04", icon: Mail, title: "Morning Briefing", desc: "Every morning an email reconstructs your progress and sets the day's agenda. Context restored before you sit down. Think of it like a secretary fkr your project." }
            ].map((s, i) => (
              <div key={i} className="flex flex-col items-center text-center">
                <div className={cn("w-24 h-24 rounded-full flex items-center justify-center mb-8 border-4", d ? "border-zinc-800 bg-zinc-950" : "border-zinc-100 bg-white shadow-md")}>
                  <s.icon size={32} className="text-purple-400" />
                </div>
                <div className="text-sm font-black text-purple-400 mb-4 tracking-widest">STEP {s.step}</div>
                <h4 className="text-xl font-bold mb-4">{s.title}</h4>
                <p className={cn("text-sm leading-relaxed", d ? "text-zinc-400" : "text-zinc-600")}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className={cn("py-24 px-6 border-t", d ? "bg-zinc-950 border-zinc-900" : "bg-zinc-50 border-zinc-200")}>
        <div className="text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-black">Your GPS for Flow State</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
          <FeatureCard d={d} icon={BrainCircuit} title="Proactive Thrusts" desc="Morning briefings that tell you exactly what was done and what to do next before you open your laptop. Flow Thrusts keep you on track as you work." />
          <FeatureCard d={d} icon={GitBranch} title="Cross-Ecosystem Sync" desc="We build a chronological truth by connecting VS Code edits, GitHub PRs, and Figma component drifts natively. More tool support is being added regularly." />
          <FeatureCard d={d} icon={Cpu} title="Fast and Safe Delta Digestion" desc="We only analyze files within your project scope. Fast, private, and zero training on your proprietary data." />
        </div>
      </section>

      {/* AGENT AWARENESS */}
      <section id="mcp" className={cn("py-24 px-6 border-y overflow-hidden", d ? "bg-zinc-900/10 border-zinc-900" : "bg-white border-zinc-200")}>
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          <div className="space-y-8">
            <h2 className="text-4xl md:text-6xl font-black tracking-tight leading-[0.9]">Agent<br />Awareness.</h2>
            <p className={cn("text-lg font-medium leading-relaxed", d ? "text-zinc-400" : "text-zinc-600")}>
              Every AI model you use is now a project expert on your specific project. Through our MCP server, Thrust serves your current project state and goals directly into your AI agentic workflows.
              <br /><br />
              Whether it's Claude Code, OpenAI, or n8n, your models remain in perfect sync with your ultimate project aim.
            </p>
            <div className="flex flex-wrap gap-4">
              {["n8n", "Claude Code", "GPT-5", "Cursor", "Gemini"].map(b => (
                <div key={b} className={cn("px-4 py-2 rounded-lg border font-bold text-sm", d ? "border-zinc-700 bg-zinc-800" : "border-zinc-200 bg-zinc-50")}>{b}</div>
              ))}
            </div>
          </div>
          <div className={cn("rounded-2xl border p-1 shadow-2xl", d ? "border-zinc-700 bg-zinc-800" : "border-zinc-300 bg-zinc-200")}>
            <div className="rounded-xl p-8 font-mono text-sm space-y-6 bg-[#0D0D14] text-zinc-300">
              <div className="flex items-center gap-3 text-emerald-500 font-bold border-b border-white/5 pb-4">
                <Cpu size={18} /> MCP Server: Active [Port 3000]
              </div>
              <div className="space-y-2 opacity-80">
                <p className="text-zinc-500">&gt; Serving project_requirements.md</p>
                <p className="text-zinc-500">&gt; Syncing local file tree hashes...</p>
                <p className="text-zinc-500">&gt; Awaiting agent handshake...</p>
              </div>
              <div className="bg-white/5 p-4 rounded-lg border border-white/5 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-purple-400">Agent: Claude Code</span>
                  <span className="text-[10px] px-2 py-0.5 bg-emerald-500/20 text-emerald-500 rounded-full">Sync Complete</span>
                </div>
                <p className="text-xs text-zinc-400 italic">"I see your aim is the E-Commerce Refactor. Stripe logic is priority. I've analyzed the delta in your last commit..."</p>
              </div>
              <div className="flex items-center gap-2 text-[10px] text-zinc-600">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                AWARENESS BROADCASTING IN REAL-TIME
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRIVACY */}
      <section className={cn("py-32 px-6", d ? "bg-zinc-950" : "bg-zinc-50")}>
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-start">
          <div className="space-y-6 md:sticky md:top-32">
            <ShieldCheck size={48} className="text-purple-400 mb-6" />
            <h2 className="text-4xl md:text-5xl font-black leading-tight">Your Project never leaves your machine.</h2>
            <p className={cn("text-lg leading-relaxed", d ? "text-zinc-400" : "text-zinc-600")}>
              Thrust was built local-first by design. The file watcher and MCP server run entirely on your machine. We never store your project. We never train on it.
              <br /><br />
              What travels to the cloud is a behavioral summary; patterns of activity, not the activity itself. Think of it like a step counter that tells us you walked 8,000 steps, not where you walked or why.
            </p>
            <p className="text-sm font-bold text-purple-400 pt-4">We are happy to open-source the local daemon for independent audit.</p>
          </div>
          <div className={cn("rounded-2xl border overflow-hidden", d ? "border-zinc-800" : "border-zinc-200")}>
            <div className={cn("p-8 border-b", d ? "bg-zinc-900 border-zinc-800" : "bg-white border-zinc-200")}>
              <div className="flex items-center gap-3 mb-6">
                <Lock className="text-emerald-500" size={24} />
                <h4 className="text-xl font-bold">LOCAL <span className="text-sm font-normal opacity-50 ml-2">Never leaves your machine</span></h4>
              </div>
              <ul className="space-y-4 font-medium text-sm">
                {["Source code and all file contents", "API keys, .env files, secrets of any kind", "File watcher and MCP server processes", "Raw diffs and commit content"].map(item => (
                  <li key={item} className="flex items-center gap-3"><CheckCircle2 size={16} className="text-emerald-500 shrink-0" />{item}</li>
                ))}
              </ul>
            </div>
            <div className={cn("p-8", d ? "bg-zinc-950" : "bg-zinc-50")}>
              <div className="flex items-center gap-3 mb-6">
                <Cloud className="text-purple-400" size={24} />
                <h4 className="text-xl font-bold">CLOUD <span className="text-sm font-normal opacity-50 ml-2">What we process</span></h4>
              </div>
              <ul className="space-y-4 font-medium text-sm">
                {["Activity summaries and completion patterns", "Project metadata (names, milestone status)", "Briefing content generated from summaries", "Morning briefing email delivery"].map(item => (
                  <li key={item} className="flex items-center gap-3"><div className="w-1.5 h-1.5 rounded-full bg-purple-400 shrink-0" />{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* INSTALL */}
      <section id="install" className={cn("py-32 px-6 border-y", d ? "bg-[#0A0A0F] border-zinc-900" : "bg-zinc-100 border-zinc-200")}>
        <div className="max-w-3xl mx-auto text-center space-y-6 mb-16">
          <h2 className="text-4xl md:text-5xl font-black">Get Running in Two Minutes.</h2>
          <p className={cn("text-xl", d ? "text-zinc-400" : "text-zinc-600")}>Create your account, install the agent, and Thrust handles the rest.</p>
        </div>
        <div className="max-w-2xl mx-auto space-y-6">
          <div className={cn("rounded-2xl border overflow-hidden", d ? "border-zinc-800 bg-zinc-900" : "border-zinc-200 bg-white")}>
            <div className={cn("flex items-center gap-4 px-6 py-4 border-b", d ? "border-zinc-800" : "border-zinc-200")}>
              <div className="w-7 h-7 rounded-full bg-purple-500 flex items-center justify-center text-white text-xs font-black shrink-0">1</div>
              <span className={cn("font-bold text-sm uppercase tracking-widest", d ? "text-zinc-400" : "text-zinc-500")}>Create Account</span>
            </div>
            <div className="p-6 text-center">
              <p className={cn("text-sm mb-6", d ? "text-zinc-400" : "text-zinc-600")}>You'll need a Thrust account to receive briefings and sync your project context. It takes under 30 seconds.</p>
              <a href="/console" className="inline-flex items-center gap-2 bg-purple-500 hover:bg-purple-400 text-white font-bold px-8 py-3 rounded-xl transition-all shadow-lg shadow-purple-500/20">

                Create Free Account <ArrowRight size={16} />
              </a>
            </div>
          </div>
          <div className={cn("rounded-2xl border overflow-hidden", d ? "border-zinc-800 bg-zinc-900" : "border-zinc-200 bg-white")}>
            <div className={cn("flex items-center gap-4 px-6 py-4 border-b", d ? "border-zinc-800" : "border-zinc-200")}>
              <div className="w-7 h-7 rounded-full bg-purple-500 flex items-center justify-center text-white text-xs font-black shrink-0">2</div>
              <span className={cn("font-bold text-sm uppercase tracking-widest", d ? "text-zinc-400" : "text-zinc-500")}>Install Local Agent</span>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <p className={cn("text-xs font-bold uppercase tracking-widest mb-2", d ? "text-zinc-500" : "text-zinc-400")}>Global Install</p>
                <div className={cn("flex items-center gap-3 font-mono text-sm border rounded-xl px-5 py-4", d ? "bg-zinc-950 border-zinc-800" : "bg-zinc-100 border-zinc-200")}>
                  <span className="text-purple-400 select-none">$</span>
        <span className={cn("flex-1", d ? "text-zinc-200" : "text-zinc-800")}>npm install -g thrust-cli</span>
                  <CopyButton text="npm install -g thrust-cli" />
                </div>
              </div>
              <div>
                <p className={cn("text-xs font-bold uppercase tracking-widest mb-2", d ? "text-zinc-500" : "text-zinc-400")}>Start Agent in Your Project</p>
                <div className={cn("flex items-center gap-3 font-mono text-sm border rounded-xl px-5 py-4", d ? "bg-zinc-950 border-zinc-800" : "bg-zinc-100 border-zinc-200")}>
                  <span className="text-purple-400 select-none">$</span>
                  <span className={cn("flex-1", d ? "text-zinc-200" : "text-zinc-800")}>thrust</span>
                  <CopyButton text="thrust" />
                </div>
              </div>
              <div className="pt-2 text-center">
                <a href="/docs" className="inline-flex items-center gap-2 text-sm font-bold text-purple-400 hover:text-purple-300 border border-purple-400/30 hover:border-purple-400/60 rounded-xl px-6 py-3 transition-all">
                  Guide Me Through Setup <ArrowRight size={14} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MEET THE DEVELOPER */}
      <section className={cn("py-24 px-6 border-b", d ? "bg-zinc-950 border-zinc-900" : "bg-white border-zinc-200")}>
        <div className="max-w-3xl mx-auto">
          <div className="flex flex-col md:flex-row gap-10 items-start">
            <div className="relative shrink-0">
              <div className="absolute inset-0 rounded-full border-2 border-purple-400/30 scale-110 pointer-events-none" />
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-600 to-zinc-800 border-2 border-purple-400 overflow-hidden flex items-center justify-center text-3xl font-black text-white select-none">
		{/* D */}

	<img src="./web_profile_picture.png"
	height={460} width={460} alt="Godswill Nathan" />

		</div>
            </div>
            <div className="space-y-5">
              <div>
                <h3 className="text-2xl font-black mb-1">Godswill Nathan</h3>
                <p className="text-sm font-bold text-purple-400 uppercase tracking-widest">Dev, Thrust</p>
              </div>
              <div className={cn("space-y-4 text-lg leading-relaxed font-medium", d ? "text-zinc-400" : "text-zinc-600")}>
                <p>
                  I have been building for a few years across Unity, the web, and mobile. I shipped a game title, built web and mobile app projects, and spent a lot of time in that uncomfortable space between having too many ideas and not enough finished things to show for it.
                </p>
		{/*   <p>
                  The common thread was never skill or motivation. It was context loss. Every time I came back to a project after a few days, I was starting from zero mentally, reconstructing what I had built, why I built it that way, and what I was supposed to do next.
                </p>
		*/}
                <p>
                  Thrust is the tool I kept wishing existed. No dashboards, no tables, no team features, no productivity theatre. Just my project's memory, restored every morning, with one clear next move waiting for me. I love feedback and suggestions so I'm looking forward to yours.
                </p>
              </div>
              <a href="mailto:publicstring.care@gmail.com" className={cn("inline-flex items-center gap-2 text-sm font-bold px-4 py-2 rounded-lg transition-colors", d ? "text-white bg-zinc-800 hover:bg-zinc-700" : "text-zinc-900 bg-zinc-100 hover:bg-zinc-200")}>
                <Mail size={16} /> Drop me an email
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className={cn("py-32 px-6 border-b", d ? "bg-[#0A0A0F] border-zinc-900" : "bg-zinc-50 border-zinc-200")}>
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-6xl font-black text-center mb-20">Focused Pricing.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
            <div className={cn("p-8 rounded-3xl border flex flex-col h-full", d ? "bg-zinc-950 border-zinc-800" : "bg-white border-zinc-200")}>
              <h4 className="text-xl font-bold mb-2">Observer</h4>
              <p className="text-4xl font-black mb-8">$0</p>
              <ul className="space-y-4 mb-10 flex-1 text-sm font-bold opacity-70">
                <li className="flex gap-3 items-center"><CheckCircle2 size={18} className="shrink-0" /> Haiku-speed Briefings</li>
                <li className="flex gap-3 items-center"><CheckCircle2 size={18} className="shrink-0" /> 1 Active Project</li>
                <li className="flex gap-3 items-center"><CheckCircle2 size={18} className="shrink-0" /> 2 Data Sources</li>
              </ul>
              <button onClick={() => scrollTo("install")} className={cn("w-full py-4 rounded-xl font-bold border transition-all", d ? "border-zinc-800 hover:bg-zinc-800" : "border-zinc-200 hover:bg-zinc-100 bg-white")}>Start Free</button>
            </div>
            <div className={cn("p-8 rounded-3xl border-2 border-purple-500 relative flex flex-col h-full scale-105 shadow-2xl z-10", d ? "bg-zinc-900" : "bg-white")}>
              <div className="absolute top-0 right-0 bg-purple-500 text-white text-[10px] font-black px-4 py-1 rounded-bl-xl uppercase">Most Popular</div>
              <h4 className="text-xl font-bold mb-2">Flow State</h4>
              <p className="text-4xl font-black mb-8">$10 <span className="text-sm opacity-50">/mo</span></p>
              <ul className="space-y-4 mb-10 flex-1 text-sm font-bold">
                <li className="flex gap-3 items-center"><CheckCircle2 className="text-purple-400 shrink-0" size={18} /> Sonnet 4.6 Deep Briefings</li>
                <li className="flex gap-3 items-center"><CheckCircle2 className="text-purple-400 shrink-0" size={18} /> 10-Min Context Refresh</li>
                <li className="flex gap-3 items-center"><CheckCircle2 className="text-purple-400 shrink-0" size={18} /> Unlimited Integrations</li>
                <li className="flex gap-3 items-center"><CheckCircle2 className="text-purple-400 shrink-0" size={18} /> MCP Server Access</li>
              </ul>
              <button onClick={() => scrollTo("install")} className="w-full py-4 bg-purple-500 text-white rounded-xl font-bold hover:bg-purple-400 shadow-xl shadow-purple-500/20 transition-all">Get Flow State</button>
            </div>
            <div className={cn("p-8 rounded-3xl border flex flex-col h-full", d ? "bg-zinc-950 border-zinc-800" : "bg-white border-zinc-200")}>
              <h4 className="text-xl font-bold mb-2">Architect</h4>
              <p className="text-4xl font-black mb-8">$20 <span className="text-sm opacity-50">/mo</span></p>
              <ul className="space-y-4 mb-10 flex-1 text-sm font-bold opacity-70">
                <li className="flex gap-3 items-center"><CheckCircle2 size={18} className="shrink-0" /> Instant Real-time Thrusts</li>
                <li className="flex gap-3 items-center"><CheckCircle2 size={18} className="shrink-0" /> Multi-Context Sync</li>
                <li className="flex gap-3 items-center"><CheckCircle2 size={18} className="shrink-0" /> Priority Requirements Sync</li>
                <li className="flex gap-3 items-center"><CheckCircle2 size={18} className="shrink-0" /> Team Seat Sharing</li>
              </ul>
              <button onClick={() => scrollTo("install")} className={cn("w-full py-4 rounded-xl font-bold border transition-all", d ? "border-zinc-800 hover:bg-zinc-800" : "border-zinc-200 hover:bg-zinc-100 bg-white")}>Get Architect</button>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className={cn("pt-24 pb-12 px-6 border-t", d ? "bg-black border-zinc-900" : "bg-zinc-100 border-zinc-200")}>
        <div className={cn("max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-12 border-b pb-20", d ? "border-zinc-900" : "border-zinc-300")}>
          <div className="space-y-6">
            <div className="flex items-center gap-2 font-black text-2xl tracking-tighter italic">
              <Zap className="text-purple-400 fill-current" size={24} /> THRUST
            </div>
            <p className={cn("text-sm font-medium leading-relaxed", d ? "text-zinc-500" : "text-zinc-500")}>
              Eliminating context decay for the modern professional. Built for high-velocity builders.
            </p>
          </div>
          <div>
            <h5 className="font-bold mb-6 text-sm uppercase tracking-widest text-purple-400">Product</h5>
            <ul className="space-y-4 text-sm font-bold opacity-70">
              <li><a href="#how-it-works" className="hover:text-purple-400 transition-colors">How it Works</a></li>
              <li><a href="#install" className="hover:text-purple-400 transition-colors">Install Agent</a></li>
              <li><a href="#pricing" className="hover:text-purple-400 transition-colors">Pricing</a></li>
              <li><a href="#mcp" className="hover:text-purple-400 transition-colors">MCP Server</a></li>
            </ul>
          </div>
          <div>
            <h5 className="font-bold mb-6 text-sm uppercase tracking-widest text-purple-400">Company</h5>
            <ul className="space-y-4 text-sm font-bold opacity-70">
              <li><a href="mailto:publicstring.care@gmail.com" className="hover:text-purple-400 transition-colors">Feedback</a></li>
              <li><a href="mailto:publicstring.care@gmail.com" className="hover:text-purple-400 transition-colors">Support</a></li>
            </ul>
          </div>
          <div>
            <h5 className="font-bold mb-6 text-sm uppercase tracking-widest text-purple-400">Legal</h5>
            <ul className="space-y-4 text-sm font-bold opacity-70">
              <li><a href="/terms" className="hover:text-purple-400 transition-colors">Terms of Service</a></li>
              <li><a href="/privacy" className="hover:text-purple-400 transition-colors">Privacy Policy</a></li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between pt-12 gap-6">
          <p className="text-[10px] font-black uppercase tracking-[0.3em] opacity-30">Stop Guessing. Start Thrusting. © 2026</p>
          <div className="flex items-center gap-6 opacity-30">
            <ShieldCheck size={18} />
            <Globe size={18} />
            <Mail size={18} />
          </div>
        </div>
      </footer>
    </div>
  );
}
